import Immutable from 'immutable';
import React from 'react';

import TableAction from 'sharedreact/components/TableAction';
import TableActions from 'sharedreact/components/TableActions';
import TableContainer from 'sharedreact/containers/TableContainer';
import SortOrder from 'sharedreact/data/SortOrder';
import TableColumn from 'sharedreact/data/TableColumn';
import { translate, tryToTranslate } from 'sharedreact/utils/LanguageSupport';

import Agent from 'workspaces/launcher/data/Agent';

type Props = {
  language: string,
  className?: string,
  agents: Immutable.List<Agent>,
  isFetching: boolean,
  selection: Immutable.Set<string>,
  showSelection: boolean,
  sortOrder: SortOrder,
  emptyTableText?: string,
  serverVersion: string,

  onRemoveAgent: (agent: Agent) => void,
  onSortOrderChange: (sortOrder: SortOrder) => void,
  onItemSelect?: (id: string) => void
};

function _getVersionText (agent: Agent): string {
  if (agent.status === 'connected') {
    return (agent.version === '' ? translate('launcher.admin.agents.version.unknown') : agent.version);
  } else {
    return agent.version;
  }
}

class AgentsTable extends React.Component<Props> {
  getVersionCellRenderingCallback () {
    return (agent: Agent, row: number, col: number) => {
      const versionText = _getVersionText(agent);
      const showWarning = (agent.status === 'connected' && (agent.version !== this.props.serverVersion));
      const className = showWarning ? 'esko_boxlayout esko_horizontal' : '';
      const toolTip = showWarning ? translate('launcher.admin.agents.version.warningtooltip') : '';
      return (
          <div className={className} title={toolTip}>
            <span className={showWarning ? 'esko_icon esko_icon-warning' : ''} />
            <span>
              {versionText}
            </span>
          </div>
      );
    };
  }

  override render (): React.ReactNode {
    const { className, agents, emptyTableText,
      language, // eslint-disable-line no-unused-vars
      selection, showSelection, sortOrder,
      onRemoveAgent, onSortOrderChange, onItemSelect,
      isFetching
    } = this.props;

    const tableColumns = Immutable.List<TableColumn<Agent>>([
      {
        title: translate('launcher.admin.agents.name'),
        width: '40%',
        renderCell: (agent: Agent, row: number, col: number) => agent.name,
        selectable: false,
        movable: true,
        sortOrderKeyword: 'name',
        name: 'name'
      },
      {
        title: translate('launcher.admin.agents.status'),
        width: '20%',
        renderCell: (agent: Agent, row: number, col: number) => tryToTranslate('launcher.admin.agents.status.' + agent.status),
        selectable: true,
        movable: true,
        sortOrderKeyword: 'status',
        name: 'status'
      },
      {
        title: translate('launcher.admin.agents.version'),
        width: '20%',
        renderCell: this.getVersionCellRenderingCallback(),
        selectable: true,
        movable: true,
        sortOrderKeyword: 'version',
        name: 'version'
      },
      {
        title: translate('launcher.admin.agents.actions'),
        width: '20%',
        renderCell: (agent: Agent, row: number, col: number) => (
          <TableActions>
            <TableAction<Agent>
              id={'ae_agent_remove_' + row}
              className='esko_tableactionremove'
              item={agent}
              title={translate('launcher.admin.agents.tooltip.remove')}
              onClick={onRemoveAgent}
            />
          </TableActions>
        ),
        selectable: false,
        movable: true,
        name: 'actions'
      }
    ]);

    return (
      <TableContainer<Agent>
        id='ae_agents_table'
        tableName='AgentsTable'
        tableColumns={tableColumns}
        rowData={agents}
        selection={selection}
        showSelection={showSelection}
        sortOrder={sortOrder}
        className={className}
        onSortOrderChange={onSortOrderChange}
        onItemSelect={onItemSelect}
        emptyTableText={emptyTableText}
        isFetching={isFetching}
      />
    );
  }
}

export default AgentsTable;
